if (!customElements.get('botslab-beta-carousel')) {
  customElements.define('botslab-beta-carousel', class extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.observer?.disconnect();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      const track = this.querySelector('slider-element');
      if (!track) return;
      const cards = Array.from(track.querySelectorAll(track.getAttribute('selector')));
      const dots = Array.from(this.querySelectorAll('.bpo-beta__dot'));
      const mobile = window.matchMedia('(max-width: 767px)');
      const update = () => {
        if (!mobile.matches || !cards.length) {
          track.style.removeProperty('height');
          return;
        }
        const start = track.getBoundingClientRect().left + parseFloat(getComputedStyle(track).paddingLeft);
        let selected = 0;
        cards.forEach((card, index) => {
          if (Math.abs(card.getBoundingClientRect().left - start) < Math.abs(cards[selected].getBoundingClientRect().left - start)) selected = index;
        });
        // The taller account card must not leave blank space below the other slides.
        const height = `${cards[selected].getBoundingClientRect().height}px`;
        if (track.style.height !== height) track.style.height = height;
        dots.forEach((dot, index) => dot.setAttribute('aria-current', index === selected ? 'true' : 'false'));
      };
      const schedule = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(update);
      };
      track.addEventListener('scroll', schedule, { ...options, passive: true });
      track.addEventListener('slider:change', schedule, options);
      mobile.addEventListener('change', schedule, options);
      this.observer = new ResizeObserver(schedule);
      this.observer.observe(track);
      cards.forEach((card) => this.observer.observe(card));
      update();
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.events?.abort();
      this.observer?.disconnect();
    }
  });
}

if (!customElements.get('botslab-review-video')) {
  customElements.define('botslab-review-video', class extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      this.video = this.querySelector('video');
      const button = this.querySelector('[data-review-play]');
      if (!this.video || !button) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      const update = () => {
        if (!this.video.paused && !this.video.ended && document.activeElement === button) {
          this.video.focus({ preventScroll: true });
        }
        button.hidden = !this.video.paused && !this.video.ended;
      };
      button.addEventListener('click', async () => {
        button.disabled = true;
        try {
          await this.video.play();
        } catch {
          // Keep both the central button and native controls available to retry.
          update();
        } finally {
          button.disabled = false;
        }
      }, options);
      ['play', 'pause', 'ended', 'error'].forEach((event) => this.video.addEventListener(event, update, options));
      update();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.video?.pause();
    }
  });
}

// Reviews use a finite track: every configured card appears once, including videos.
if (!customElements.get('botslab-beta-reviews')) {
  customElements.define('botslab-beta-reviews', class extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.track = this.querySelector('slider-element');
      if (!this.track) return;
      this.cards = Array.from(this.track.querySelectorAll('.bpo-beta-review'));
      this.dots = Array.from(this.querySelectorAll('.bpo-beta__dot'));
      this.arrows = Array.from(this.querySelectorAll('[data-review-direction]'));
      this.videos = Array.from(this.querySelectorAll('video'));
      this.mobile = window.matchMedia('(max-width: 767px)');
      const queue = (fit = false) => {
        this.needsFit = this.needsFit || fit;
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => {
          if (this.needsFit) {
            this.cards.forEach((card) => this.fitQuote(card.querySelector('.bpo-beta-review__quote')));
            this.needsFit = false;
          }
          this.update();
        });
      };
      this.arrows.forEach((button) => button.addEventListener('click', () => {
        this.move(Number(button.dataset.reviewDirection));
      }, options));
      this.track.addEventListener('keydown', (event) => {
        if (event.target.closest('video, button, input, a, select, textarea')) return;
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.move(event.key === 'ArrowLeft' ? -1 : 1);
      }, options);
      this.track.addEventListener('scroll', () => queue(), { ...options, passive: true });
      this.track.addEventListener('slider:change', () => queue(), options);
      this.mobile.addEventListener('change', () => queue(true), options);
      this.observer = new ResizeObserver(() => queue(true));
      this.observer.observe(this.track);
      this.cards.forEach((card) => this.observer.observe(card));
      this.mutations = new MutationObserver(() => queue(true));
      this.mutations.observe(this.track, { childList: true, characterData: true, subtree: true });
      if (window.IntersectionObserver) {
        this.visibility = new IntersectionObserver((entries) => {
          entries.forEach((entry) => { if (entry.intersectionRatio < .5) entry.target.pause(); });
        }, { threshold: .5 });
        this.videos.forEach((video) => this.visibility.observe(video));
      }
      this.videos.forEach((video) => video.addEventListener('play', () => {
        this.videos.forEach((other) => { if (other !== video) other.pause(); });
      }, options));
      document.fonts?.ready.then(() => { if (this.isConnected) queue(true); });
      queue(true);
    }

    fitQuote(quote) {
      if (!quote || !quote.clientWidth) return;
      quote.style.removeProperty('font-size');
      quote.style.removeProperty('height');
      const styles = getComputedStyle(quote);
      const base = parseFloat(styles.fontSize);
      let low = Math.min(base, parseFloat(styles.getPropertyValue('--review-min-font')) || 14);
      let high = base;
      const fits = () => quote.scrollHeight <= quote.clientHeight + 1 && quote.scrollWidth <= quote.clientWidth + 1;
      if (fits()) return;
      quote.style.fontSize = `${low}px`;
      if (!fits()) {
        // Extremely long reviews stay complete and readable at the minimum size.
        quote.style.height = 'auto';
        return;
      }
      while (high - low > .25) {
        const mid = (low + high) / 2;
        quote.style.fontSize = `${mid}px`;
        if (fits()) low = mid;
        else high = mid;
      }
      quote.style.fontSize = `${low}px`;
    }

    move(direction) {
      const first = this.cards[0];
      if (!first) return;
      const next = this.cards[1];
      const step = next ? next.offsetLeft - first.offsetLeft : first.clientWidth;
      const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      this.track.scrollTo({
        left: Math.max(0, Math.min(max, this.track.scrollLeft + direction * step)),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    }

    update() {
      const track = this.track;
      const max = Math.max(0, track.scrollWidth - track.clientWidth);
      this.arrows.forEach((button) => {
        button.disabled = Number(button.dataset.reviewDirection) < 0 ? track.scrollLeft <= 1 : track.scrollLeft >= max - 1;
      });
      const bounds = track.getBoundingClientRect();
      const start = bounds.left + parseFloat(getComputedStyle(track).paddingLeft);
      let selected = 0;
      this.cards.forEach((card, index) => {
        const rect = card.getBoundingClientRect();
        if (Math.abs(rect.left - start) < Math.abs(this.cards[selected].getBoundingClientRect().left - start)) selected = index;
        const visibleWidth = Math.max(0, Math.min(rect.right, bounds.right) - Math.max(rect.left, bounds.left));
        if (visibleWidth < (rect.right - rect.left) / 2) {
          card.querySelectorAll('video').forEach((video) => video.pause());
        }
      });
      if (this.mobile.matches && this.cards[selected]) {
        const height = `${this.cards[selected].getBoundingClientRect().height}px`;
        if (track.style.height !== height) track.style.height = height;
      } else track.style.removeProperty('height');
      this.dots.forEach((dot, index) => dot.setAttribute('aria-current', index === selected ? 'true' : 'false'));
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.events?.abort();
      this.observer?.disconnect();
      this.mutations?.disconnect();
      this.visibility?.disconnect();
      this.videos?.forEach((video) => video.pause());
    }
  });
}

if (!customElements.get('botslab-beta-feedback')) {
  customElements.define('botslab-beta-feedback', class extends HTMLElement {
    connectedCallback() {
      this.stopDrag?.();
      this.events?.abort();
      this.observer?.disconnect();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      const track = this.querySelector('slider-element');
      const range = this.querySelector('input[type="range"]');
      const control = this.querySelector('.bpo-beta-feedback__scroll');
      if (!track) return;
      let drag = null;
      let moved = false;
      this.stopDrag = () => {
        const pointerId = drag?.id;
        drag = null;
        track.classList.remove('is-dragging');
        if (pointerId !== undefined && track.hasPointerCapture(pointerId)) {
          track.releasePointerCapture(pointerId);
        }
      };
      const update = () => {
        const distance = track.scrollWidth - track.clientWidth;
        const draggable = distance > 1 && getComputedStyle(track).overflowX !== 'visible';
        track.classList.toggle('is-draggable', draggable);
        if (!draggable) this.stopDrag();
        if (!range || !control) return;
        const position = distance > 0 ? Math.min(1, Math.max(0, track.scrollLeft / distance)) : 0;
        const thumbWidth = Math.min(1, track.clientWidth / Math.max(1, track.scrollWidth));
        range.value = Math.round(position * 1000);
        range.disabled = distance <= 1;
        control.style.setProperty('--beta-thumb-width', `${thumbWidth * 100}%`);
        control.style.setProperty('--beta-thumb-left', `${position * (1 - thumbWidth) * 100}%`);
      };
      range?.addEventListener('input', () => {
        track.scrollLeft = (track.scrollWidth - track.clientWidth) * Number(range.value) / 1000;
      }, options);
      // Touch and trackpad scrolling stay native; mouse dragging uses the same scroll position.
      track.addEventListener('pointerdown', (event) => {
        moved = false;
        if (event.pointerType !== 'mouse' || event.button !== 0 || !event.isPrimary) return;
        if (!track.classList.contains('is-draggable')) return;
        if (event.target.closest('a, button, input, select, textarea, [contenteditable="true"]')) return;
        drag = { id: event.pointerId, x: event.clientX, left: track.scrollLeft };
      }, options);
      window.addEventListener('pointermove', (event) => {
        if (!drag || event.pointerId !== drag.id) return;
        if (!(event.buttons & 1)) { this.stopDrag(); return; }
        const dx = event.clientX - drag.x;
        if (!moved && Math.abs(dx) < 4) return;
        if (!moved) {
          moved = true;
          track.setPointerCapture(event.pointerId);
          track.classList.add('is-dragging');
        }
        event.preventDefault();
        track.scrollLeft = drag.left - dx;
      }, options);
      const endDrag = (event) => {
        if (drag && event.pointerId === drag.id) this.stopDrag();
      };
      window.addEventListener('pointerup', endDrag, options);
      window.addEventListener('pointercancel', endDrag, options);
      track.addEventListener('lostpointercapture', endDrag, options);
      window.addEventListener('blur', this.stopDrag, options);
      track.addEventListener('dragstart', (event) => {
        if (drag) event.preventDefault();
      }, options);
      track.addEventListener('click', (event) => {
        if (!moved) return;
        moved = false;
        event.preventDefault();
        event.stopPropagation();
      }, { ...options, capture: true });
      track.addEventListener('scroll', update, { ...options, passive: true });
      this.observer = new ResizeObserver(update);
      this.observer.observe(track);
      update();
    }
    disconnectedCallback() {
      this.stopDrag?.();
      this.events?.abort();
      this.observer?.disconnect();
    }
  });
}
